import { useMemo, useRef, useState } from "react";
import * as LucideIcons from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Toast } from "../../components/Toast";
import { IconPicker, type IconEntry } from "./free-logo-creator/IconPicker";
import { IconPreview } from "./free-logo-creator/IconPreview";
import { ExportPanel } from "./free-logo-creator/ExportPanel";
import {
  buildSvgMarkup,
  downloadFile,
  svgToPngDataUrl,
} from "./free-logo-creator/utils";

const DEFAULT_ICON = "Sparkles";

type BackgroundMode = "transparent" | "solid";

const getIconEntries = (): IconEntry[] => {
  const iconCollection =
    typeof LucideIcons.icons === "object" && LucideIcons.icons
      ? LucideIcons.icons
      : LucideIcons;

  return Object.entries(iconCollection)
    .filter(([name, Component]) => {
      if (!/^[A-Z]/.test(name)) return false;
      if (name === "Icon" || name === "createLucideIcon" || name === "icons") {
        return false;
      }
      const type = typeof Component;
      return type === "function" || type === "object";
    })
    .map(([name, Component]) => ({
      name,
      Component: Component as LucideIcon,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export function FreeLogoCreator() {
  const { t } = useTranslation();
  const iconEntries = useMemo(() => getIconEntries(), []);
  const defaultIcon =
    iconEntries.find((entry) => entry.name === DEFAULT_ICON) ?? iconEntries[0];

  const [selectedIcon, setSelectedIcon] = useState(defaultIcon?.name ?? "");
  const [search, setSearch] = useState("");
  const [size, setSize] = useState(160);
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [color, setColor] = useState("#38BDF8");
  const [backgroundMode, setBackgroundMode] = useState<BackgroundMode>(
    "transparent",
  );
  const [backgroundColor, setBackgroundColor] = useState("#0B1020");
  const [padding, setPadding] = useState(24);
  const [zoom, setZoom] = useState(1.2);
  const [rotation, setRotation] = useState(0);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const svgRef = useRef<SVGSVGElement>(null);
  const SelectedIcon = useMemo(() => {
    const match = iconEntries.find((entry) => entry.name === selectedIcon);
    return match?.Component ?? iconEntries[0]?.Component;
  }, [iconEntries, selectedIcon]);

  const svgMeta = () => {
    const svg = svgRef.current;
    if (!svg) return null;
    return {
      inner: svg.innerHTML,
      viewBox: svg.getAttribute("viewBox") ?? "0 0 24 24",
      strokeLinecap: svg.getAttribute("stroke-linecap") ?? "round",
      strokeLinejoin: svg.getAttribute("stroke-linejoin") ?? "round",
    };
  };

  const buildExportSvg = (options?: { totalSize?: number }) => {
    const meta = svgMeta();
    if (!meta) return null;

    const totalSize = options?.totalSize ?? size + padding * 2;
    const iconSize = Math.max(totalSize - padding * 2, 1);

    return buildSvgMarkup({
      iconMarkup: meta.inner,
      iconViewBox: meta.viewBox,
      iconSize,
      totalSize,
      padding,
      color,
      strokeWidth,
      strokeLinecap: meta.strokeLinecap,
      strokeLinejoin: meta.strokeLinejoin,
      backgroundMode,
      backgroundColor,
      rotation,
    });
  };

  const exportSvg = () => {
    const svgMarkup = buildExportSvg();
    if (!svgMarkup) return;
    const blob = new Blob([svgMarkup], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    downloadFile(`lucide-${selectedIcon.toLowerCase()}.svg`, url);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const exportPng = async (presetSize?: number) => {
    const totalSize = presetSize ?? size + padding * 2;
    const svgMarkup = buildExportSvg({ totalSize });
    if (!svgMarkup) return;

    try {
      const dataUrl = await svgToPngDataUrl(svgMarkup, totalSize);
      downloadFile(`lucide-${selectedIcon.toLowerCase()}-${totalSize}px.png`, dataUrl);
    } catch {
      setToastMessage(t("logoCreator.pngFailed"));
    }
  };

  const copySvg = async () => {
    const svgMarkup = buildExportSvg();
    if (!svgMarkup) return;
    try {
      await navigator.clipboard.writeText(svgMarkup);
      setToastMessage(t("logoCreator.copySuccess"));
    } catch {
      setToastMessage(t("logoCreator.copyFailed"));
    }
  };

  if (!SelectedIcon) {
    return (
      <div className="free-logo-creator flc-empty-state">
        {t("logoCreator.loadError")}
      </div>
    );
  }

  return (
    <div className="free-logo-creator">
      <IconPicker
        icons={iconEntries}
        search={search}
        selected={selectedIcon}
        onSearchChange={setSearch}
        onSelect={setSelectedIcon}
        noResultsLabel={t("logoCreator.noResults")}
        searchPlaceholder={t("logoCreator.search")}
        title={t("logoCreator.iconLibrary")}
      />

      <IconPreview
        Icon={SelectedIcon}
        svgRef={svgRef}
        settings={{
          size,
          strokeWidth,
          color,
          padding,
          backgroundMode,
          backgroundColor,
          zoom,
          rotation,
        }}
        title={t("logoCreator.preview")}
        zoomLabel={t("logoCreator.zoom")}
        rotationLabel={t("logoCreator.rotation")}
        outputLabel={t("logoCreator.output")}
        onZoomChange={setZoom}
        onRotationChange={setRotation}
      />

      <div className="flc-panel flc-controls">
        <div className="flc-panel-header">
          <h2>{t("logoCreator.customize")}</h2>
        </div>

        <label className="flc-field">
          <span className="flc-label">{t("logoCreator.size")}</span>
          <input
            type="range"
            min={16}
            max={512}
            step={1}
            value={size}
            onChange={(event) => setSize(Number(event.target.value))}
          />
          <span className="flc-metric">{size}px</span>
        </label>

        <label className="flc-field">
          <span className="flc-label">{t("logoCreator.stroke")}</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={strokeWidth}
            onChange={(event) => setStrokeWidth(Number(event.target.value))}
          />
          <span className="flc-metric">{strokeWidth.toFixed(1)}</span>
        </label>

        <label className="flc-field">
          <span className="flc-label">{t("logoCreator.color")}</span>
          <input
            type="color"
            value={color}
            onChange={(event) => setColor(event.target.value)}
          />
        </label>

        <div className="flc-field">
          <span className="flc-label">{t("logoCreator.background")}</span>
          <div className="flc-toggle">
            <button
              type="button"
              className={`flc-chip ${backgroundMode === "transparent" ? "active" : ""}`}
              onClick={() => setBackgroundMode("transparent")}
            >
              {t("logoCreator.transparent")}
            </button>
            <button
              type="button"
              className={`flc-chip ${backgroundMode === "solid" ? "active" : ""}`}
              onClick={() => setBackgroundMode("solid")}
            >
              {t("logoCreator.solid")}
            </button>
          </div>
          {backgroundMode === "solid" && (
            <input
              type="color"
              value={backgroundColor}
              onChange={(event) => setBackgroundColor(event.target.value)}
            />
          )}
        </div>

        <label className="flc-field">
          <span className="flc-label">{t("logoCreator.padding")}</span>
          <input
            type="range"
            min={0}
            max={64}
            step={1}
            value={padding}
            onChange={(event) => setPadding(Number(event.target.value))}
          />
          <span className="flc-metric">{padding}px</span>
        </label>

        <ExportPanel
          title={t("logoCreator.export")}
          svgLabel={t("logoCreator.exportSvg")}
          pngLabel={t("logoCreator.exportPng")}
          copyLabel={t("logoCreator.copySvg")}
          presetsLabel={t("logoCreator.pngPresets")}
          onExportSvg={exportSvg}
          onExportPng={() => exportPng()}
          onCopySvg={copySvg}
          onExportPreset={exportPng}
          disabled={!svgRef.current}
        />
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
